import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Loader from "../../../components/Loader";
import { getRsaPdfLink } from "../../../actions/creation/getRsaPdf";

const RsaPdfViewer = () => {
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Extract outlet_id and rsa_file_slug from URL search string
  const extractParams = useCallback(() => {
    const search = location.search || window.location.search || "";
    if (!search) {
      return { outletId: null, rsaFileSlug: null };
    }

    // Try standard query parameters (?outlet_id=...&rsa_file_slug=...)
    const urlParams = new URLSearchParams(search);
    const qOutletId = urlParams.get("outlet_id");
    const qRsaSlug = urlParams.get("rsa_file_slug");
    if (qOutletId && qRsaSlug) {
      return { outletId: qOutletId.trim(), rsaFileSlug: qRsaSlug.trim() };
    }

    // Handle slash separated format in query string: ?29950714/N6sk2AIGYv
    const rawQuery = search.startsWith("?") ? search.slice(1) : search;
    const parts = rawQuery.split("/").map((p) => p.trim()).filter(Boolean);

    if (parts.length >= 2) {
      return { outletId: parts[0], rsaFileSlug: parts[1] };
    }

    return { outletId: null, rsaFileSlug: null };
  }, [location.search]);

  const fetchPdf = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { outletId, rsaFileSlug } = extractParams();

    if (!outletId || !rsaFileSlug) {
      setLoading(false);
      setError("Invalid or missing parameters in the link.");
      return;
    }

    try {
      const response = await getRsaPdfLink(outletId, rsaFileSlug);
      const url = response?.url || response?.data?.url;

      if (url) {
        setPdfUrl(url);
        setLoading(false);
        // Automatically redirect to S3 PDF link
        window.location.replace(url);
      } else {
        setLoading(false);
        setError("PDF document link could not be generated. Please try again later.");
      }
    } catch (err) {
      console.error("Error fetching RSA PDF link:", err);
      setLoading(false);
      setError(
        err?.message ||
        err?.error ||
        "Unable to fetch the PDF document. Please verify the link or try again."
      );
    }
  }, [extractParams]);

  useEffect(() => {
    fetchPdf();
  }, [fetchPdf]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f4f6f8",
        padding: "16px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        boxSizing: "border-box"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
          padding: "36px 24px",
          textAlign: "center",
          boxSizing: "border-box"
        }}
      >
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ height: "100px", position: "relative", width: "100px" }}>
              <Loader position="relative" />
            </div>
            <h3
              style={{
                margin: "24px 0 8px 0",
                fontSize: "18px",
                fontWeight: 600,
                color: "#1e293b"
              }}
            >
              Loading RSA Document
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                lineHeight: "1.5"
              }}
            >
              Please wait while we retrieve your PDF...
            </p>
          </div>
        )}

        {!loading && error && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px"
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: 600,
                color: "#991b1b"
              }}
            >
              Unable to Open Document
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                fontSize: "14px",
                color: "#64748b",
                lineHeight: "1.5"
              }}
            >
              {error}
            </p>
            <button
              onClick={fetchPdf}
              style={{
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 0.2s"
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#2563eb")}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && pdfUrl && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px"
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: 600,
                color: "#1e293b"
              }}
            >
              Document Ready
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                fontSize: "14px",
                color: "#64748b",
                lineHeight: "1.5"
              }}
            >
              Redirecting to your PDF. If your browser did not open it automatically, click below:
            </p>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                textDecoration: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 0.2s"
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#2563eb")}
            >
              Open / Download PDF
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default RsaPdfViewer;
